import useScrollReveal from '../useScrollReveal';
import { sectionStyles, getAnimation, getCardClass, isCardTransparent, hexToRgba } from '../utils';
import SectionHeader from './SectionHeader';
import SectionDivider from './SectionDivider';
import SectionBgImage from './SectionBgImage';
import SectionIcon from './SectionIcon';

export default function IconGridSection({ block = {}, primaryColor, secondaryColor, enableAnimations, cardStyle = 'shadow', mediaFolders = {} }) {
    const { config = {}, content = {} } = block;
    const s = sectionStyles(config, primaryColor, secondaryColor);
    const [ref, visible] = useScrollReveal(0.15, !enableAnimations);
    const items = content.items || [];
    const anim = getAnimation(config, visible);
    const variant = config.variant || 'compact';
    const columns = config.columns || 4;

    if (items.length === 0) return null;

    const cls = `${s.padClass} relative overflow-hidden ${s.radiusClass} ${s.shadowClass}`;
    const bgS = s.bgStyle.background ? s.bgStyle : { backgroundColor: s.bg || '#ffffff', ...s.bgStyle };
    const card = getCardClass(config.card_style || cardStyle, s.dark);
    const isTransparent = isCardTransparent(config.card_style || cardStyle, s.dark);

    const colClass = columns === 2 ? 'grid-cols-1 sm:grid-cols-2'
        : columns === 3 ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3'
        : columns === 5 ? 'grid-cols-2 sm:grid-cols-3 lg:grid-cols-5'
        : columns === 6 ? 'grid-cols-2 sm:grid-cols-3 lg:grid-cols-6'
        : 'grid-cols-2 sm:grid-cols-3 lg:grid-cols-4';

    return (
        <section className={cls} style={bgS}>
            <SectionBgImage config={config} mediaFolders={mediaFolders} />
            <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
            <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 relative z-10 ${anim}`}>
                <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s} />

                {variant === 'compact' && (
                    <div className={`grid ${colClass} gap-4`}>
                        {items.map((item, i) => {
                            const ac = item.color || s.accent;
                            return (
                                <div key={i} className={`${card} p-5 text-center transition-all duration-300`}
                                    style={{
                                        transitionDelay: `${i * 50}ms`,
                                        color: isTransparent ? s.text : undefined,
                                    }}>
                                    <div className="flex items-center justify-center mb-3">
                                        <div className="w-12 h-12 rounded-xl flex items-center justify-center"
                                            style={{ backgroundColor: hexToRgba(ac, 0.08) }}>
                                            <SectionIcon name={item.icon} size={24} color={ac} />
                                        </div>
                                    </div>
                                    <h4 className="text-sm font-semibold" style={{ color: isTransparent ? s.text : undefined }}>
                                        {item.title}
                                    </h4>
                                </div>
                            );
                        })}
                    </div>
                )}

                {variant === 'detailed' && (
                    <div className={`grid ${colClass} gap-6`}>
                        {items.map((item, i) => {
                            const ac = item.color || s.accent;
                            return (
                                <div key={i} className={`${card} p-6 transition-all duration-300`}
                                    style={{
                                        transitionDelay: `${i * 50}ms`,
                                        color: isTransparent ? s.text : undefined,
                                    }}>
                                    <div className="flex items-center gap-4 mb-3">
                                        <div className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0"
                                            style={{ backgroundColor: hexToRgba(ac, 0.08) }}>
                                            <SectionIcon name={item.icon} size={24} color={ac} />
                                        </div>
                                        <h4 className="text-sm font-semibold" style={{ color: isTransparent ? s.text : undefined }}>
                                            {item.title}
                                        </h4>
                                    </div>
                                    {item.description && (
                                        <p className="text-sm leading-relaxed" style={{ color: s.textSub }}>
                                            {item.description}
                                        </p>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}

                {variant === 'inline' && (
                    <div className={`grid ${colClass} gap-6`}>
                        {items.map((item, i) => {
                            const ac = item.color || s.accent;
                            return (
                                <div key={i} className="flex items-start gap-3 transition-all duration-300"
                                    style={{ transitionDelay: `${i * 50}ms` }}>
                                    <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0"
                                        style={{ backgroundColor: hexToRgba(ac, 0.08) }}>
                                        <SectionIcon name={item.icon} size={20} color={ac} />
                                    </div>
                                    <div>
                                        <h4 className="text-sm font-semibold" style={{ color: s.text }}>{item.title}</h4>
                                        {item.description && (
                                            <p className="text-xs mt-0.5 leading-relaxed" style={{ color: s.textSub }}>
                                                {item.description}
                                            </p>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}

                {variant === 'centered' && (
                    <div className={`grid ${colClass} gap-8`}>
                        {items.map((item, i) => {
                            const ac = item.color || s.accent;
                            return (
                                <div key={i} className="text-center transition-all duration-300"
                                    style={{ transitionDelay: `${i * 50}ms` }}>
                                    <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4"
                                        style={{ backgroundColor: hexToRgba(ac, 0.08) }}>
                                        <SectionIcon name={item.icon} size={32} color={ac} />
                                    </div>
                                    <h4 className="text-base font-semibold mb-1" style={{ color: s.text }}>{item.title}</h4>
                                    {item.description && (
                                        <p className="text-sm leading-relaxed" style={{ color: s.textSub }}>
                                            {item.description}
                                        </p>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
            <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
        </section>
    );
}
